import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/apiClient';
import './QualityAssistant.css';

/**
 * 🎯 QUALITY ASSISTANT - Assistant Qualité Actionnable
 * 
 * Composant isolé qui transforme VectorSearch en assistant qualité
 * avec outputs actionnables (création de nœuds, tests, relations)
 * 
 * @param {Object} props
 * @param {Array} props.nodes - Nœuds existants
 * @param {Function} props.onNodeSelect - Callback sélection nœud
 * @param {Function} props.addPendingNode - Ajouter aux éléments en modélisation
 * @param {Function} props.onHighlightNodes - Highlight nœuds dans le graphe
 * @param {Function} props.onNavigateToSection - Navigation vers section
 */
const QualityAssistant = ({ 
  nodes = [], 
  onNodeSelect, 
  addPendingNode,
  onHighlightNodes,
  onNavigateToSection 
}) => {
  const { t, i18n } = useTranslation();
  
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('ask'); // ask | analyze | suggest
  const [previewAction, setPreviewAction] = useState(null); // Pour prévisualisation
  
  // 🆕 Réponses séparées par mode pour éviter l'écrasement
  const [responses, setResponses] = useState({
    ask: null,
    analyze: null,
    suggest: null
  });
  
  // Récupérer la réponse du mode actif
  const response = responses[mode];

  /**
   * 🔍 RECHERCHE LOCALE - Recherche rapide dans les nœuds chargés
   * Optimisation invisible : recherche locale avant vector search
   */
  const searchInLoadedNodes = (searchQuery) => {
    const queryLower = searchQuery.toLowerCase().trim();
    const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);
    
    if (queryWords.length === 0) return [];
    
    return nodes
      .map(node => {
        let score = 0;
        const nodeName = (node.name || '').toLowerCase();
        const nodeDesc = (node.description || '').toLowerCase();
        const nodeType = (node.type || '').toLowerCase();
        const nodeTeam = (node.featureTeam || '').toLowerCase();
        const hasTests = node.testsJson && node.testsJson.length > 0;
        
        // Scoring par pertinence
        queryWords.forEach(word => {
          // Correspondance exacte dans le nom (poids fort)
          if (nodeName.includes(word)) score += 15;
          // Correspondance dans la description
          if (nodeDesc.includes(word)) score += 5;
          // Correspondance dans le type
          if (nodeType.includes(word)) score += 8;
          // Correspondance dans l'équipe
          if (nodeTeam.includes(word)) score += 3;
        });
        
        // Boost pour certaines questions structurelles
        if (queryLower.includes('test') && !hasTests) score += 10;
        if (queryLower.includes('sans test') && !hasTests) score += 20;
        if (queryLower.includes('isolé') && (!node.dependencies || node.dependencies.length === 0)) score += 15;
        
        return { node, score };
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map(({ node }) => node);
  };

  /**
   * 📊 Afficher les résultats de recherche locale
   */
  const displayLocalResults = (matches) => {
    const answer = matches.length > 0
      ? `✅ **${matches.length}** ${t('qualityAssistant.results.localSearch.found')} :\n\n` +
        matches.map((node, i) => {
          const testInfo = node.testsJson && node.testsJson.length > 0 
            ? `✓ ${node.testsJson.length} test(s)`
            : `⚠️ ${t('qualityAssistant.results.noTests')}`;
          return `${i + 1}. **${node.name}** (${node.type})\n   ${testInfo}\n   ${(node.description || t('qualityAssistant.results.noDescription')).substring(0, 100)}...`;
        }).join('\n\n')
      : `ℹ️ ${t('qualityAssistant.results.localSearch.noMatch')}`;
    
    setResponses(prev => ({
      ...prev,
      ask: {
        answer,
        confidence: {
          level: 'high',
          score: 0.95,
          explanation: t('qualityAssistant.results.localSearch.instant')
        },
        sources: matches.map(node => ({
          name: node.name,
          type: node.type,
          description: node.description,
          hasTests: node.testsJson && node.testsJson.length > 0
        })),
        suggestedActions: matches.length > 0 ? [
          {
            type: 'navigate',
            label: t('qualityAssistant.results.viewInGraph'),
            target: { section: 'graph', nodeId: matches[0].name }
          }
        ] : [],
        searchMethod: 'local' // 🆕 Indicateur du mode utilisé
      }
    }));
  };

  /**
   * 🔍 RECHERCHE HYBRIDE - Locale d'abord, puis vectorielle si besoin
   * Optimisation invisible pour l'utilisateur
   */
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    
    try {
      // 🚀 PHASE 1 : Recherche locale rapide (invisible)
      const localMatches = searchInLoadedNodes(query.trim());
      
      // Si on a au moins 3 résultats pertinents, on s'arrête là
      if (localMatches.length >= 3) {
        displayLocalResults(localMatches);
        setLoading(false);
        toast.success(t('qualityAssistant.toast.success.searchComplete'));
        return;
      }
      
      // 🧠 PHASE 2 : Recherche vectorielle (fallback automatique)
      const result = await apiClient.post('/vector/ask', {
        question: query.trim(),
        lang: i18n.language // 🌍 Utiliser la langue de l'interface (fr/en)
      });

      // 🆕 Stocker dans le state du mode 'ask'
      setResponses(prev => ({ 
        ...prev, 
        ask: { 
          ...result.data, 
          searchMethod: 'vector' // 🆕 Indicateur du mode utilisé
        } 
      }));
      
      // 🎯 Highlight automatique des nœuds pertinents
      if (result.data.highlightNodes && onHighlightNodes) {
        onHighlightNodes(result.data.highlightNodes);
      }

      toast.success(t('qualityAssistant.toast.success.searchComplete'));

    } catch (error) {
      console.error('❌ Erreur recherche:', error);
      toast.error(error.response?.data?.error || t('qualityAssistant.toast.error.search'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🎬 Gestion des actions suggérées
   */
  const handleAction = (action, skipPreview = false) => {
    console.log('🎬 Action déclenchée:', action);

    // 👁️ Si create-node ou create-test, afficher preview d'abord (sauf si skipPreview)
    if (!skipPreview && (action.type === 'create-node' || action.type === 'create-test')) {
      setPreviewAction(action);
      return;
    }

    switch (action.type) {
      case 'create-node':
        if (!addPendingNode) {
          toast.error(t('qualityAssistant.toast.error.addPendingNode'));
          return;
        }
        addPendingNode({
          id: `qa-${Date.now()}`,
          name: action.data.name,
          description: action.data.description || '',
          type: action.data.type || 'Feature',
          technology: action.data.technology || '',
          status: 'draft',
          metadata: {
            sourceType: 'quality-assistant',
            query: query,
            confidence: action.data.confidence || 0.8,
            createdAt: new Date().toISOString()
          }
        });
        toast.success(t('qualityAssistant.toast.success.nodeAdded', { name: action.data.name }));
        break;

      case 'create-test':
        if (!addPendingNode) {
          toast.error(t('qualityAssistant.toast.error.addPendingNode'));
          return;
        }
        addPendingNode({
          id: `qa-test-${Date.now()}`,
          name: action.data.name || 'Nouveau Test',
          type: 'Test',
          targetNode: action.data.targetNode || '',
          description: action.data.description || '',
          steps: action.data.steps || [],
          status: 'draft',
          metadata: {
            sourceType: 'quality-assistant',
            query: query,
            testType: action.data.testType || 'acceptance',
            priority: action.data.priority || 'medium',
            createdAt: new Date().toISOString()
          }
        });
        toast.success(t('qualityAssistant.toast.success.testAdded', { name: action.data.name || 'Test' }));
        setPreviewAction(null); // Fermer la preview
        break;

      case 'navigate':
        if (onNavigateToSection && action.target?.section) {
          onNavigateToSection(action.target.section);
        }
        if (action.target?.nodeId && onNodeSelect) {
          const targetNode = nodes.find(n => n.name === action.target.nodeId);
          if (targetNode) {
            onNodeSelect(targetNode);
            toast.info(t('qualityAssistant.toast.info.navigateTo', { name: targetNode.name }));
          }
        }
        break;

      case 'highlight-node':
        if (onHighlightNodes && action.target?.nodeId) {
          onHighlightNodes([action.target.nodeId]);
          toast.info(t('qualityAssistant.toast.info.highlighted', { nodeId: action.target.nodeId }));
        }
        break;

      case 'add-relation':
        if (action.data) {
          toast.info(t('qualityAssistant.toast.info.relationSuggested', { 
            source: action.data.source, 
            target: action.data.target 
          }));
          // TODO: Implémenter ajout relation dans pendingNodes
        }
        break;

      default:
        console.warn(t('qualityAssistant.toast.warn.unknownAction', { type: action.type }));
        toast.warn(t('qualityAssistant.toast.warn.notImplemented'));
    }
  };

  /**
   * 📊 Mode Analyse - Audit qualité global
   */
  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const result = await apiClient.post('/vector/analyze-quality', {
        includeRecommendations: true,
        lang: i18n.language // 🌍 Utiliser la langue de l'interface (fr/en)
      });

      // 🆕 Stocker dans le state du mode 'analyze'
      setResponses(prev => ({ ...prev, analyze: result.data }));
      toast.success(t('qualityAssistant.toast.success.analyzeComplete'));

    } catch (error) {
      console.error('❌ Erreur analyse:', error);
      toast.error(t('qualityAssistant.toast.error.analyze'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * 💡 Mode Suggestions - Recommandations proactives
   */
  const handleSuggest = async () => {
    setLoading(true);
    try {
      const result = await apiClient.post('/vector/suggestions', {
        context: { nodeCount: nodes.length },
        lang: i18n.language // 🌍 Utiliser la langue de l'interface (fr/en)
      });

      // 🆕 Stocker dans le state du mode 'suggest'
      setResponses(prev => ({ ...prev, suggest: result.data }));
      toast.success(t('qualityAssistant.toast.success.suggestComplete'));

    } catch (error) {
      console.error('❌ Erreur suggestions:', error);
      toast.error(t('qualityAssistant.toast.error.suggest'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * 📝 Formate la réponse avec Markdown simple et émojis
   */
  const renderFormattedAnswer = (answer) => {
    if (!answer) return null;

    const inlineBold = (text) =>
      text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    const lines = answer.split('\n');
    const elements = [];
    let listBuffer = [];

    const flushList = (keyPrefix) => {
      if (listBuffer.length > 0) {
        elements.push(
          <ul key={`list-${keyPrefix}`} className="qa-formatted-ul">
            {listBuffer}
          </ul>
        );
        listBuffer = [];
      }
    };

    lines.forEach((line, idx) => {
      // Titres ##
      if (line.startsWith('##')) {
        flushList(idx);
        elements.push(
          <h4 key={idx} className="qa-formatted-h4">
            {line.replace(/^#+/, '').trim()}
          </h4>
        );
        return;
      }

      // Ligne entière en gras = sous-titre
      if (line.match(/^\*\*(.+)\*\*:?\s*$/) && !line.trim().startsWith('-')) {
        flushList(idx);
        elements.push(
          <h5 key={idx} className="qa-formatted-h5">
            {line.replace(/\*\*/g, '').trim()}
          </h5>
        );
        return;
      }

      // Séparateur -- ou ---
      if (line.trim() === '--' || line.trim() === '---') {
        flushList(idx);
        elements.push(<div key={idx} className="qa-formatted-spacer" />);
        return;
      }

      // Listes à puces
      if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        const text = line.trim().substring(1).trim();
        if (text) {
          listBuffer.push(
            <li key={idx} className="qa-formatted-li"
              dangerouslySetInnerHTML={{ __html: inlineBold(text) }} />
          );
        }
        return;
      }

      flushList(idx);

      // Numérotation
      if (line.match(/^\d+\./)) {
        elements.push(
          <div key={idx} className="qa-formatted-numbered"
            dangerouslySetInnerHTML={{ __html: inlineBold(line) }} />
        );
        return;
      }

      // Ligne vide
      if (line.trim() === '') {
        elements.push(<div key={idx} className="qa-formatted-spacer" />);
        return;
      }

      // Ligne normale
      elements.push(
        <p key={idx} className="qa-formatted-p"
          dangerouslySetInnerHTML={{ __html: inlineBold(line) }} />
      );
    });

    flushList('end');
    return elements;
  };

  return (
    <div className="qa-panel-isolated">
      {/* Header avec modes */}
      <div className="qa-header-isolated">
        <div className="qa-title-isolated">
          <span className="qa-icon-isolated">🤖</span>
          <h3>{t('qualityAssistant.header.title')}</h3>
        </div>
        
        <div className="qa-modes-isolated">
          <button 
            className={`qa-mode-btn-isolated ${mode === 'ask' ? 'active' : ''}`}
            onClick={() => setMode('ask')}
          >
            <span>💬</span> {t('qualityAssistant.header.modes.ask')}
          </button>
          <button 
            className={`qa-mode-btn-isolated ${mode === 'analyze' ? 'active' : ''}`}
            onClick={() => { setMode('analyze'); if (!responses.analyze) handleAnalyze(); }}
            disabled={loading}
          >
            <span>📊</span> {t('qualityAssistant.header.modes.analyze')}
          </button>
          <button 
            className={`qa-mode-btn-isolated ${mode === 'suggest' ? 'active' : ''}`}
            onClick={() => { setMode('suggest'); if (!responses.suggest) handleSuggest(); }}
            disabled={loading}
          >
            <span>💡</span> {t('qualityAssistant.header.modes.suggest')}
          </button>
        </div>
      </div>

      {/* Mode Ask - Questions */}
      {mode === 'ask' && (
        <div className="qa-ask-mode-isolated">
          <form onSubmit={handleSearch} className="qa-search-form-isolated">
            <div className="qa-input-group-isolated">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('qualityAssistant.search.placeholder')}
                className="qa-input-isolated"
                disabled={loading}
              />
              <button 
                type="submit" 
                disabled={loading || !query.trim()}
                className="qa-search-btn-isolated"
              >
                {loading ? '⏳' : '🔍'} {loading ? t('qualityAssistant.search.loading') : t('qualityAssistant.search.button')}
              </button>
            </div>
          </form>

          {!response && !loading && (
            <div className="qa-empty-state-isolated">
              <span className="qa-empty-icon-isolated">💭</span>
              <p>{t('qualityAssistant.empty.message')}</p>
              <div className="qa-examples-isolated">
                <p className="qa-examples-title-isolated">{t('qualityAssistant.empty.examples.title')}</p>
                <ul>
                  <li onClick={() => setQuery(t('qualityAssistant.empty.examples.noTests'))}>
                    {t('qualityAssistant.empty.examples.noTests')}
                  </li>
                  <li onClick={() => setQuery(t('qualityAssistant.empty.examples.coverage'))}>
                    {t('qualityAssistant.empty.examples.coverage')}
                  </li>
                  <li onClick={() => setQuery(t('qualityAssistant.empty.examples.dependencies'))}>
                    {t('qualityAssistant.empty.examples.dependencies')}
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Résultats */}
      {response && (
        <div className="qa-results-isolated">
          {/* Bouton Refresh */}
          <div className="qa-results-header-isolated">
            <button 
              className="qa-refresh-btn-isolated"
              onClick={() => {
                if (mode === 'analyze') handleAnalyze();
                else if (mode === 'suggest') handleSuggest();
                else if (query.trim()) handleSearch({ preventDefault: () => {} });
              }}
              disabled={loading}
              title={t('qualityAssistant.search.refreshTitle')}
            >
              <span className="qa-refresh-icon-isolated">🔄</span>
              {loading ? t('qualityAssistant.search.loading') : t('qualityAssistant.search.refresh')}
            </button>
          </div>

          {/* Confiance */}
          {response.confidence && (
            <div className="qa-confidence-isolated">
              <span className={`qa-confidence-badge-isolated ${response.confidence.level}`}>
                {response.confidence.score >= 0.9 ? '🟢' : 
                 response.confidence.score >= 0.7 ? '🟡' : '🔴'}
              </span>
              <span className="qa-confidence-text-isolated">
                {t('qualityAssistant.results.confidence')}: {Math.round((response.confidence.score || 0) * 100)}%
              </span>
              {response.confidence.explanation && (
                <span className="qa-confidence-explanation-isolated">
                  {response.confidence.explanation}
                </span>
              )}
            </div>
          )}

          {/* Réponse principale */}
          {response.answer && (
            <div className="qa-answer-isolated">
              <h4 className="qa-answer-title-isolated">
                <span className="qa-title-icon-isolated">💬</span>
                {t('qualityAssistant.results.answer')}
              </h4>
              <div className="qa-answer-content-isolated">
                {typeof response.answer === 'string' 
                  ? renderFormattedAnswer(response.answer) 
                  : typeof response.answer === 'object'
                    ? JSON.stringify(response.answer, null, 2)
                    : String(response.answer)
                }
              </div>
            </div>
          )}

          {/* Score qualité (mode analyze) */}
          {response.qualityScore !== undefined && (
            <div className="qa-quality-score-isolated">
              <h4>📊 {t('qualityAssistant.results.qualityScore')}</h4>
              <div className="qa-score-gauge-isolated">
                <div 
                  className="qa-score-fill-isolated"
                  style={{ width: `${response.qualityScore}%` }}
                />
                <span className="qa-score-value-isolated">{response.qualityScore}%</span>
              </div>
            </div>
          )}

          {/* Actions suggérées */}
          {response.suggestedActions && response.suggestedActions.length > 0 && (
            <div className="qa-actions-isolated">
              <h4 className="qa-actions-title-isolated">💡 {t('qualityAssistant.results.actions')}</h4>
              <div className="qa-actions-grid-isolated">
                {response.suggestedActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAction(action)}
                    className="qa-action-btn-isolated"
                    title={action.description || action.label}
                  >
                    <span className="qa-action-icon-isolated">
                      {action.type === 'create-node' ? '📄' :
                       action.type === 'create-test' ? '🧪' :
                       action.type === 'navigate' ? '🎯' :
                       action.type === 'add-relation' ? '🔗' : '⚡'}
                    </span>
                    <span className="qa-action-label-isolated">{action.label}</span>
                    {action.priority && (
                      <span className={`qa-action-priority-isolated ${action.priority}`}>
                        {action.priority === 'high' ? '🔴' : action.priority === 'medium' ? '🟡' : '🟢'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Recommandations (format ancien) */}
          {response.recommendations && response.recommendations.length > 0 && (
            <div className="qa-recommendations-isolated">
              <h4>📋 {t('qualityAssistant.results.recommendations')}</h4>
              <ul>
                {response.recommendations.map((rec, idx) => (
                  <li key={idx} className={`qa-rec-item-isolated priority-${rec.priority}`}>
                    <strong>{rec.action}</strong>: {rec.description}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sources consultées */}
          {response.sources && response.sources.length > 0 && (
            <div className="qa-sources-isolated">
              <h4 className="qa-sources-title-isolated">📚 {t('qualityAssistant.results.sources')}</h4>
              <div className="qa-sources-grid-isolated">
                {response.sources.map((source, idx) => (
                  <div 
                    key={idx} 
                    className="qa-source-card-isolated"
                    onClick={() => handleAction({
                      type: 'highlight-node',
                      target: { nodeId: source.node }
                    })}
                  >
                    <div className="qa-source-header-isolated">
                      <span className="qa-source-name-isolated">{source.node}</span>
                      <span className="qa-source-similarity-isolated">
                        {Math.round((source.score || source.similarity || 0) * 100)}%
                      </span>
                    </div>
                    <p className="qa-source-type-isolated">
                      {source.type} {source.technology && `• ${source.technology}`}
                    </p>
                    {source.description && (
                      <p className="qa-source-excerpt-isolated">{source.description.substring(0, 100)}...</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nœuds liés */}
          {response.relatedNodes && response.relatedNodes.length > 0 && (
            <div className="qa-related-nodes-isolated">
              <h4>🔗 Nœuds Liés</h4>
              <div className="qa-related-grid-isolated">
                {response.relatedNodes.map((node, idx) => (
                  <div 
                    key={idx} 
                    className="qa-related-card-isolated"
                    onClick={() => handleAction({
                      type: 'navigate',
                      target: { nodeId: node.name }
                    })}
                  >
                    <span className="qa-related-name-isolated">{node.name}</span>
                    <span className={`qa-related-tests-isolated ${node.hasTests ? 'has-tests' : 'no-tests'}`}>
                      {node.hasTests ? t('qualityAssistant.related.hasTests') : t('qualityAssistant.related.noTests')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="qa-loading-isolated">
          <div className="qa-spinner-isolated" />
          <p>{t('qualityAssistant.loading.analyzing')}</p>
        </div>
      )}

      {/* 👁️ Modal de prévisualisation */}
      {previewAction && (
        <div className="qa-preview-modal-isolated">
          <div className="qa-preview-backdrop-isolated" onClick={() => setPreviewAction(null)} />
          <div className="qa-preview-content-isolated">
            <div className="qa-preview-header-isolated">
              <h3>
                {previewAction.type === 'create-node' ? '📄' : '🧪'} 
                {t('qualityAssistant.preview.title')}
              </h3>
              <button 
                className="qa-preview-close-isolated"
                onClick={() => setPreviewAction(null)}
              >
                ✕
              </button>
            </div>

            <div className="qa-preview-body-isolated">
              <div className="qa-preview-field-isolated">
                <label>{t('qualityAssistant.preview.sections.name')}</label>
                <div className="qa-preview-value-isolated">{previewAction.data.name}</div>
              </div>

              {previewAction.data.targetNode && (
                <div className="qa-preview-field-isolated">
                  <label>{t('qualityAssistant.preview.sections.targetNode')}</label>
                  <div className="qa-preview-value-isolated">{previewAction.data.targetNode}</div>
                </div>
              )}

              <div className="qa-preview-field-isolated">
                <label>{t('qualityAssistant.preview.sections.type')}</label>
                <div className="qa-preview-value-isolated">
                  {previewAction.data.type || previewAction.data.testType || 'N/A'}
                </div>
              </div>

              <div className="qa-preview-field-isolated">
                <label>{t('qualityAssistant.preview.sections.description')}</label>
                <div className="qa-preview-value-isolated qa-preview-description-isolated">
                  {previewAction.data.description || t('qualityAssistant.preview.sections.noDescription')}
                </div>
              </div>

              {previewAction.data.steps && previewAction.data.steps.length > 0 && (
                <div className="qa-preview-field-isolated">
                  <label>{t('qualityAssistant.preview.sections.testSteps', { count: previewAction.data.steps.length })}</label>
                  <div className="qa-preview-steps-isolated">
                    {previewAction.data.steps.map((step, idx) => (
                      <div key={idx} className="qa-preview-step-isolated">
                        {step || <em className="qa-preview-separator-isolated">---</em>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="qa-preview-field-isolated">
                <label>{t('qualityAssistant.preview.sections.confidence')}</label>
                <div className="qa-preview-value-isolated">
                  {Math.round((previewAction.data.confidence || 0.8) * 100)}%
                </div>
              </div>
            </div>

            <div className="qa-preview-footer-isolated">
              <button 
                className="qa-preview-cancel-isolated"
                onClick={() => setPreviewAction(null)}
              >
                {t('qualityAssistant.preview.cancel')}
              </button>
              <button 
                className="qa-preview-confirm-isolated"
                onClick={() => {
                  handleAction(previewAction, true); // skipPreview = true
                }}
              >
                {t('qualityAssistant.preview.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualityAssistant;

